import { UISref } from "@uirouter/react";
import moment from "moment";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import NumberFormat from "react-number-format";
import { timer } from "rxjs";
import { Option } from "space-monad";
import styled from "styled-components";
import { ReactComponent as www } from "../../../assets/www.svg";
import { PrimaryButton } from "../../../components/button";
import { Icon } from "../../../components/icons";
import { socialIconMap } from "../../../components/icons/social";
import { Flex } from "../../../components/layout";
import { Widget, WIDGET_BORDER_RADIUS } from "../../../components/widget";
import { LaunchesDTO } from "../../../store/launches/models/dto";
import { colours } from "../../../styles/colours";

moment.relativeTimeThreshold("ss", 0);
moment.relativeTimeThreshold("mm", 0);
moment.relativeTimeThreshold("hh", 0);
moment.relativeTimeThreshold("dd", 0);
moment.relativeTimeThreshold("ww", 0);
moment.relativeTimeThreshold("MM", 0);

const Wrapper = styled(Widget)`
  display: flex;
  flex-direction: column;
  padding: 0;
`;

const Image = styled.img`
  border-top-left-radius: ${WIDGET_BORDER_RADIUS};
  border-top-right-radius: ${WIDGET_BORDER_RADIUS};
  width: 100%;
  position: absolute;
  top: 0;
  right: 0;
`;

const ImageWrapper = styled.div`
  border-top-left-radius: ${WIDGET_BORDER_RADIUS};
  border-top-right-radius: ${WIDGET_BORDER_RADIUS};
  width: 100%;
  margin-bottom: 73%;
`;

const Body = styled(Flex)`
  padding: 2em;
  flex-grow: 1;
  flex-direction: column;
  align-items: flex-start;
`;

const Description = styled.p`
  flex-grow: 1;
  margin: 0;
`;

const CTA = styled(PrimaryButton).attrs({ fullWidth: true })`
  padding: 1em;
  text-transform: uppercase;
  border-radius: 0;
  border-bottom-left-radius: ${WIDGET_BORDER_RADIUS};
  border-bottom-right-radius: ${WIDGET_BORDER_RADIUS};
`;

const UnclickableCTA = styled(CTA)`
  cursor: default;
`;

const HomepageLink = styled.a`
  width: 100%;
`;

const WhitepaperIconWrapper = styled.div`
  bottom: 0.5em;
  right: 0.5em;
  position: absolute;
  font-size: 1.5em;
  padding: 0.4em;
  border-radius: 1em;
  background: ${colours.white};
`;

const WhitepaperIcon = styled(Icon).attrs({
  type: "whitepaper",
  colour1: colours.blueGrey,
})``;

const SocialIcon = styled.div`
  height: 2em;
  width: auto;
`;

const SocialLink = styled.a`
  display: inline-block;

  &:not(:last-child) {
    margin-right: 0.5em;
  }
`;

const Reladiv = styled.div`
  position: relative;
`;

const SocialIconWrapper = styled.div`
  margin-top: 1em;
`;

const Amounts = styled.table`
  margin-bottom: 1em;

  tr:not(:last-child) td {
    padding-bottom: 0.4em;
  }

  td {
    padding-left: 0.5em;
    font-weight: 700;

    &:first-child {
      padding-left: 0em;
      color: ${colours.blue};
    }
  }
`;

const AmountsTable: FunctionComponent<
  Pick<
    LaunchesDTO.Responses.GetSingle.T,
    "initialTokenPrice" | "plannedRaiseAmount" | "pledgingInfo"
  >
> = ({ pledgingInfo, plannedRaiseAmount, initialTokenPrice }) => {
  const { t } = useTranslation("token-onboarding");
  const min = useMemo(
    () =>
      Option(pledgingInfo)
        .map((x) => x.USD.amount)
        .map((x) => x.minimum)
        .fold(
          () => null,
          (minimum) => (
            <tr>
              <td>{t("minimum-pledge-amount")}</td>
              <td>
                <NumberFormat
                  value={minimum}
                  displayType="text"
                  thousandSeparator={true}
                  prefix="$"
                  decimalScale={4}
                />
              </td>
            </tr>
          )
        ),
    [pledgingInfo]
  );

  const max = useMemo(
    () =>
      Option(pledgingInfo)
        .map((x) => x.USD.amount)
        .map((x) => x.maximum)
        .fold(
          () => null,
          (maximum) => (
            <tr>
              <td>{t("maximum-pledge-amount")}</td>
              <td>
                <NumberFormat
                  value={maximum}
                  displayType="text"
                  thousandSeparator={true}
                  prefix="$"
                  decimalScale={4}
                />
              </td>
            </tr>
          )
        ),
    [pledgingInfo]
  );

  return plannedRaiseAmount || initialTokenPrice || min || max ? (
    <Amounts>
      <tbody>
        {plannedRaiseAmount ? (
          <tr>
            <td>{t("raise-amount")}</td>
            <td>
              <NumberFormat
                value={plannedRaiseAmount}
                displayType="text"
                thousandSeparator={true}
                prefix="$"
              />
            </td>
          </tr>
        ) : null}
        {initialTokenPrice ? (
          <tr>
            <td>{t("initial-price")}</td>
            <td>
              <NumberFormat
                value={initialTokenPrice}
                displayType="text"
                thousandSeparator={true}
                prefix="$"
                decimalScale={4}
              />
            </td>
          </tr>
        ) : null}
        {min}
        {max}
      </tbody>
    </Amounts>
  ) : null;
};

export const Launch: FunctionComponent<
  LaunchesDTO.Responses.GetSingle.T & {
    past?: boolean;
    ctaLink: string;
  }
> = ({
  imageUrl,
  between: { start },
  name,
  pledgingInfo,
  plannedRaiseAmount,
  initialTokenPrice,
  description,
  id,
  past,
  ctaLink,
  urls: { Homepage, Medium, Whitepaper, Telegram, Twitter, Facebook },
}) => {
  const [timeLeft, setTimeLeft] = useState<string | null>(null);
  const { t } = useTranslation("token-onboarding");

  useEffect(() => {
    const stream = timer(0, 1000).subscribe(() => {
      const startDate = moment.utc(start);
      setTimeLeft(startDate.isAfter(moment.now()) ? startDate.fromNow() : null);
    });

    return () => stream.unsubscribe();
  }, []);

  return (
    <Wrapper>
      <Reladiv>
        {past || timeLeft ? (
          <HomepageLink href={Homepage} target="_blank" rel="noreferrer">
            <ImageWrapper />
            <Image src={imageUrl} />
          </HomepageLink>
        ) : (
          <UISref to="^.launch-form" params={{ id }}>
            <HomepageLink>
              <ImageWrapper />
              <Image src={imageUrl} />
            </HomepageLink>
          </UISref>
        )}
        {Whitepaper ? (
          <a target="_blank" rel="noreferrer" href={Whitepaper}>
            <WhitepaperIconWrapper>
              <WhitepaperIcon />
            </WhitepaperIconWrapper>
          </a>
        ) : null}
      </Reladiv>
      <Body>
        <h2>{name}</h2>
        <AmountsTable
          pledgingInfo={pledgingInfo}
          plannedRaiseAmount={plannedRaiseAmount}
          initialTokenPrice={initialTokenPrice}
        />
        <Description>{description}</Description>
        <SocialIconWrapper>
          {Telegram ? (
            <SocialLink href={Telegram}>
              <SocialIcon as={socialIconMap.TELEGRAM} />
            </SocialLink>
          ) : null}
          {Medium ? (
            <SocialLink href={Medium}>
              <SocialIcon as={socialIconMap.MEDIUM} />
            </SocialLink>
          ) : null}
          {Twitter ? (
            <SocialLink href={Twitter}>
              <SocialIcon as={socialIconMap.TWITTER} />
            </SocialLink>
          ) : null}
          {Facebook ? (
            <SocialLink href={Facebook}>
              <SocialIcon as={socialIconMap.FACEBOOK} />
            </SocialLink>
          ) : null}
          {!past && !timeLeft && Homepage ? (
            <SocialLink href={Homepage}>
              <SocialIcon as={www} />
            </SocialLink>
          ) : null}
        </SocialIconWrapper>
      </Body>
      {!past && timeLeft ? (
        <UnclickableCTA>{t("opens-in", { timeLeft })}</UnclickableCTA>
      ) : null}
      {!past && !timeLeft ? (
        <UISref to={ctaLink} params={{ id }}>
          <a>
            <CTA>{t("participate-now")}</CTA>
          </a>
        </UISref>
      ) : null}
    </Wrapper>
  );
};
